import 'core-js';
import 'es6-promise/auto';
import 'get-root-node-polyfill/implement';
import './setupIndexHtml';
import './registerUnknownFunctions';
import './storeQueryUtmToCookies';
import './addCookieFunctionToWindow';
import './patchReactstrapFade';

import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import type { Action, Dispatch } from 'redux';
import { Fade } from 'reactstrap';
import axios from './helpers/axios';
import store from './modules/store';
import App from './App';
import { sleep } from './helpers/globalFunctions';
import { trackAuthFlag } from './helpers/authFlagTracking';

import momentLocalizer from 'react-widgets-moment';
import moment from 'moment';

// reactstrap 9.2.3 reads Fade.defaultProps inside PopperContent to build the
// default transition for Tooltip/Popover/Dropdown, but never assigns it on the
// component. Without this, transition.timeout is undefined and PopperContent2
// emits a propType warning on every popper-based component.
(Fade as unknown as { defaultProps: Record<string, unknown> }).defaultProps = {
  timeout: 150,
  appear: true,
  enter: true,
  exit: true,
  in: true,
  baseClass: 'fade',
  baseClassActive: 'show'
};

momentLocalizer();

// Keep the react-widgets date/calendar locale in sync with the ACCOUNT language
// (window.LANGUAGE_SW, set via SET_USER). German -> `de` (DD.MM.YYYY); English ->
// `en-gb` (DD/MM/YYYY, day-first European). This module's `moment` is the very
// instance react-widgets renders with, so applying it here is what takes effect.
// Re-assert on every store change (rather than once) because a few pages still
// call moment.locale() with their own value on mount; re-applying keeps the
// account language authoritative instead of losing to those per-page setters.
const syncDateLocaleToLanguage = () => {
  const next = (window.LANGUAGE_SW ?? 0) === 0 ? 'de' : 'en-gb';
  if (moment.locale() !== next) {
    moment.locale(next);
  }
};
syncDateLocaleToLanguage();
store.subscribe(syncDateLocaleToLanguage);

// Suppress known warnings from third-party libraries (reactable)
if (process.env.NODE_ENV !== 'production') {
  const originalError = console.error.bind(console);
  console.error = (...args: any[]) => {
    const msg = typeof args[0] === 'string' ? args[0] : '';

    if (
      msg.includes('Sortable column specified without column name') ||
      msg.includes('Filterable column specified without column name')
    ) {
      return;
    }
    originalError(...args);
  };
}

const logToServer = async function (message: string, attempt = 0): Promise<void> {
  try {
    console.log('[LOG TO SERVER] =>', message);
    await axios.post('/api/log', { message });
  } catch (e) {
    console.log('[LOG TO SERVER] => FAILED - WAITING');
    await sleep(5000 * (attempt + 1));

    if (attempt < 20) return await logToServer(message, attempt + 1);
    console.log('logToServer failed 20 times... give up');
  }
};

window.logToServer = (message: string) => logToServer(message);

console.log('check auth flag =>', localStorage.getItem('auth'));

trackAuthFlag();

// /api/logout redirects here after invalidating the server session. Another tab
// or a request that finished during logout can leave the shared localStorage
// flag stale, so never use it to pre-authenticate the login landing page. The
// /api/me request in App will establish the authoritative state instead.
const isLoginPage = window.location.hash.startsWith('#/login');

if (localStorage.getItem('auth') && !isLoginPage) {
  const authAction: Action = { type: 'AUTH_USER' };
  (store.dispatch as Dispatch<Action>)(authAction);
}

createRoot(document.getElementById('root')!).render(
  <Provider store={store}>
    <App />
  </Provider>
);
